import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import FilterDropdown from '../common/FilterDropdown';


const UserRegistrationModal = ({ onClose, user = null }) => {
    const { post, get, patch } = useApi();
    const { showToast } = useAppState();
    const { user: currentUser } = useAuth();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [students, setStudents] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [fetchingStudents, setFetchingStudents] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const [streams, setStreams] = useState([]);
    const [loadingStreams, setLoadingStreams] = useState(false);
    const [grades, setGrades] = useState([]);
    const [loadingGrades, setLoadingGrades] = useState(false);
    const [houses, setHouses] = useState([]);
    const [loadingHouses, setLoadingHouses] = useState(false);

    const streamOptions = React.useMemo(() => streams.map(s => ({ name: s.name, value: s.id })), [streams]);
    const gradeOptions = React.useMemo(() => grades.map(g => ({ name: g.name, value: g.id })), [grades]);

    useEffect(() => {
        const fetchGrades = async () => {
            setLoadingGrades(true);
            try {
                const data = await get('/api/cbc/grade-levels/');
                setGrades(data);
            } catch (err) {
                console.error('Error fetching grades:', err);
            } finally {
                setLoadingGrades(false);
            }
        };
        fetchGrades();
        
        const fetchHouses = async () => {
            setLoadingHouses(true);
            try {
                const data = await get('/api/houses/');
                setHouses(data);
            } catch (err) {
                console.error('Error fetching houses:', err);
            } finally {
                setLoadingHouses(false);
            }
        };
        fetchHouses();
    }, [get]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const [formData, setFormData] = useState({
        role: user?.role || 'student',
        first_name: user?.name?.split(' ')[0] || '',
        last_name: user?.name?.split(' ').slice(1).join(' ') || '',
        email: user?.email || '',
        password: '', // Leave empty for updates
        student_id: user?.system_id && user.role === 'student' ? user.system_id : '',
        teacher_id: user?.system_id && user.role === 'teacher' ? user.system_id : '',
        grade: user?.grade_level || '',
        phone: user?.phone || '',
        student_ids: user?.linked_student_ids || [],
        stream: user?.stream || '',
        house: user?.house || '',
        career_pathway: user?.career_pathway || '',
        relationship: user?.relationship || 'OTHER',
        school: user?.school || currentUser?.school || ''
    });

    const isEdit = !!user;

    useEffect(() => {
        if (!isEdit && !formData.password) {
            setFormData(prev => ({ ...prev, password: Math.random().toString(36).slice(-8) }));
        }
    }, [isEdit, formData.password]);

    const fetchStreams = useCallback(async (gradeId) => {
        if (!gradeId) return;
        setLoadingStreams(true);
        try {
            const streamsData = await get(`/api/cbc/streams/?grade_level=${gradeId}`);
            setStreams(streamsData);
        } catch (err) {
            console.error('Error fetching streams:', err);
        } finally {
            setLoadingStreams(false);
        }
    }, [get]);

    useEffect(() => {
        if (formData.grade) {
            fetchStreams(formData.grade);
        }
    }, [formData.grade, fetchStreams]);

    const fetchStudents = useCallback(async () => {
        setFetchingStudents(true);
        try {
            const data = await get('/api/admin/users/');
            setStudents(data.filter(u => u.role === 'student'));
        } catch (err) {
            console.error('Error fetching students:', err);
        } finally {
            setFetchingStudents(false);
        }
    }, [get]);

    useEffect(() => {
        if (formData.role === 'parent' && students.length === 0) {
            fetchStudents();
        }
    }, [formData.role, students.length, fetchStudents]);

    const toggleStudent = (dbId) => {
        setFormData(prev => ({
            ...prev,
            student_ids: prev.student_ids.includes(dbId)
                ? prev.student_ids.filter(id => id !== dbId)
                : [...prev.student_ids, dbId]
        }));
    };

    const filteredStudents = students.filter(s =>
        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.system_id?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handlePasswordReset = async () => {
        setSubmitting(true);
        try {
            const newPassword = Math.random().toString(36).slice(-8);
            await patch(`/api/admin/users/${user.id}/`, { ...formData, password: newPassword });
            showToast('Password reset successfully. Email sent to user.', 'success');
            onClose();
        } catch (error) {
            console.error('Password reset error:', error);
            showToast('Failed to reset password', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.role === 'student' && streams.length > 0 && !formData.stream) {
            showToast('Please select a stream for this grade.', 'error');
            return;
        }

        setSubmitting(true);
        try {
            if (isEdit) {
                const payload = { ...formData };
                if (!payload.password) delete payload.password; // Don't update password if not provided
                await patch(`/api/admin/users/${user.id}/`, payload);
                showToast(`${formData.role.toUpperCase()} profile updated successfully.`, 'success');
            } else {
                await post('/api/admin/add-user/', formData);
                showToast(`${formData.role.toUpperCase()} registered. Welcome email sent!`, 'success');
            }
            onClose();
        } catch (error) {
            console.error('Submission error:', error);
            showToast(error.response?.data?.error || `Failed to ${isEdit ? 'update' : 'register'} user`, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const roles = [
        { id: 'student', label: 'Student', icon: 'fa-user-graduate' },
        { id: 'teacher', label: 'Teacher', icon: 'fa-chalkboard-user' },
        { id: 'parent', label: 'Guardian', icon: 'fa-user-group' }
    ];

    return (
        <div className="fixed inset-0 bg-[#0F172A]/40 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className={`bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl overflow-hidden border border-white/20 animate-in zoom-in-95 duration-500 max-h-[90vh] flex flex-col`}>
                {/* Compact Header Section */}
                <div className="p-8 pb-6 border-b border-slate-50 flex items-center justify-between flex-shrink-0 bg-slate-50/30">
                    <div className="flex items-center gap-10">
                        <div>
                            <h2 className="text-2xl font-black text-[#18216D] tracking-tighter uppercase italic leading-none">
                                {isEdit ? 'Update Profile' : 'Enroll Users'}
                            </h2>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1.5">
                                {isEdit ? `Modifying access for ${user.name}` : 'Create Parents, Students and Teachers to grant access'}
                            </p>
                        </div>

                        {/* Inline Role Switcher */}
                        {!isEdit && (
                            <div className="flex flex-wrap p-1 bg-white border border-slate-100 rounded-2xl shadow-sm">
                                {roles.map(role => (
                                    <button
                                        key={role.id}
                                        type="button"
                                        onClick={() => !isEdit && setFormData({ ...formData, role: role.id })}
                                        disabled={isEdit}
                                        className={`px-4 sm:px-6 py-2 rounded-xl flex items-center gap-2 transition-all duration-300 ${formData.role === role.id
                                            ? 'bg-[#18216D] text-white shadow-lg shadow-indigo-900/10'
                                            : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'} ${isEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        <i className={`fas ${role.icon} text-[10px] ${formData.role === role.id ? 'text-[#FFC425]' : 'text-slate-300'}`}></i>
                                        <span className="text-[9px] font-black uppercase tracking-widest leading-none">{role.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        {isEdit && formData.role === 'student' && (
                            <button
                                type="button"
                                onClick={() => {
                                    onClose();
                                    navigate(`/admin/household/${user.db_id}`);
                                }}
                                className="px-5 py-2.5 bg-indigo-50 border border-indigo-100 text-[#18216D] rounded-xl font-black text-[9px] uppercase tracking-widest hover:bg-indigo-100 transition-all flex items-center gap-2 group shadow-sm"
                            >
                                <i className="fas fa-house-chimney group-hover:scale-110 transition-transform text-[#FFC425]"></i>
                                Manage Household
                            </button>
                        )}
                        <button onClick={onClose} className="w-10 h-10 rounded-full flex items-center justify-center text-slate-300 hover:text-rose-500 hover:bg-rose-50 transition-all border border-transparent hover:border-rose-100 bg-white shadow-sm">
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                </div>

                {/* Form Area */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-8 pt-4 space-y-8 custom-scrollbar">
                    <div className="max-w-2xl mx-auto space-y-8">
                        {/* Core Identity Row */}
                        <div className="grid grid-cols-2 gap-5">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">First Name</label>
                                <input
                                    type="text"
                                    value={formData.first_name}
                                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                    className="w-full px-5 py-4 bg-white border border-slate-200 shadow-sm rounded-2xl focus:ring-2 focus:ring-[#18216D]/10 font-bold text-sm transition-all"
                                    placeholder="John"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Last Name</label>
                                <input
                                    type="text"
                                    value={formData.last_name}
                                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                    className="w-full px-5 py-4 bg-white border border-slate-200 shadow-sm rounded-2xl focus:ring-2 focus:ring-[#18216D]/10 font-bold text-sm transition-all"
                                    placeholder="Doe"
                                    required
                                />
                            </div>
                        </div>

                        {/* Communication Row */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="w-full px-5 py-4 bg-white border border-slate-200 shadow-sm rounded-2xl focus:ring-2 focus:ring-[#18216D]/10 font-bold text-sm transition-all"
                                placeholder="john.doe@kiandaschool.ac.ke"
                                required
                            />
                        </div>

                        {/* Access & Identity Row */}
                        <div className="grid grid-cols-2 gap-5">
                            {formData.role === 'student' ? (
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Admission Number</label>
                                    <input
                                        type="text"
                                        value={formData.student_id}
                                        onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                                        className="w-full px-5 py-4 bg-white border border-slate-200 shadow-sm rounded-2xl focus:ring-2 focus:ring-[#18216D]/10 font-mono font-black text-sm"
                                        placeholder="K-2024-001"
                                        required
                                    />
                                </div>
                            ) : (
                                <>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Phone Number</label>
                                        <input
                                            type="text"
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            className="w-full px-5 py-4 bg-white border border-slate-200 shadow-sm rounded-2xl focus:ring-2 focus:ring-[#18216D]/10 font-bold text-sm"
                                            placeholder="+254..."
                                            required
                                        />
                                    </div>
                                    {formData.role === 'parent' && (
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Relationship</label>
                                            <select
                                                value={formData.relationship}
                                                onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                                                className="w-full px-5 py-4 bg-white border border-slate-200 shadow-sm rounded-2xl focus:ring-2 focus:ring-[#18216D]/10 font-bold text-sm appearance-none cursor-pointer"
                                                required
                                            >
                                                <option value="MOTHER">Mother</option>
                                                <option value="FATHER">Father</option>
                                                <option value="OTHER">Other/Guardian</option>
                                            </select>
                                        </div>
                                    )}
                                </>
                            )}

                            {isEdit && (
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                        Password Reset
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handlePasswordReset}
                                        disabled={submitting}
                                        className="w-full h-12 bg-white border border-slate-200 shadow-sm rounded-2xl text-sm focus:ring-2 focus:ring-[#18216D]/20 transition-all font-bold text-indigo-600 flex items-center justify-center gap-2 hover:bg-indigo-50"
                                    >
                                        <i className="fas fa-key"></i>
                                        Generate New Password
                                    </button>
                                    <p className="text-[9px] text-slate-400 font-medium px-2 italic">
                                        Generates a secure password, sends it to the user, and updates access immediately.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Role Specific Extension */}
                        {formData.role === 'student' && (
                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-5">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                                            Grade Designation {loadingGrades && <i className="fas fa-spinner fa-spin ml-2"></i>}
                                        </label>
                                        <select
                                            value={formData.grade}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                // Convert to number if possible, so strict equality works
                                                const numVal = val ? Number(val) : '';
                                                setFormData({ ...formData, grade: numVal, stream: '' });
                                            }}
                                            className="w-full px-5 py-4 bg-white border border-slate-200 shadow-sm rounded-2xl focus:ring-2 focus:ring-[#18216D]/10 font-bold text-sm appearance-none cursor-pointer"
                                        >
                                            <option value="">Select Grade</option>
                                            {gradeOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {streams.length > 0 && (
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                                                Stream Designation {loadingStreams && <i className="fas fa-spinner fa-spin ml-2"></i>}
                                            </label>
                                            <select
                                                value={formData.stream}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    const numVal = val ? Number(val) : '';
                                                    setFormData({ ...formData, stream: numVal });
                                                }}
                                                className="w-full px-5 py-4 bg-white border border-slate-200 shadow-sm rounded-2xl focus:ring-2 focus:ring-[#18216D]/10 font-bold text-sm appearance-none cursor-pointer"
                                            >
                                                <option value="">Select Stream</option>
                                                {streamOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                                            House Designation {loadingHouses && <i className="fas fa-spinner fa-spin ml-2"></i>}
                                        </label>
                                        <select
                                            value={formData.house}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const numVal = val ? Number(val) : '';
                                                setFormData({ ...formData, house: numVal });
                                            }}
                                            className="w-full px-5 py-4 bg-white border border-slate-200 shadow-sm rounded-2xl focus:ring-2 focus:ring-[#18216D]/10 font-bold text-sm appearance-none cursor-pointer"
                                        >
                                            <option value="">Select House</option>
                                            {houses.map(h => (
                                                <option key={h.id} value={h.id}>{h.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {grades.find(g => g.id === formData.grade)?.name.match(/Grade (10|11|12)/i) && (
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Career Pathway</label>
                                        <select
                                            value={formData.career_pathway}
                                            onChange={(e) => setFormData({ ...formData, career_pathway: e.target.value })}
                                            className="w-full px-5 py-4 bg-white border border-slate-200 shadow-sm rounded-2xl focus:ring-2 focus:ring-[#18216D]/10 font-bold text-sm appearance-none cursor-pointer"
                                            required
                                        >
                                            <option value="">Select Pathway</option>
                                            <option value="STEM">STEM (Science, Tech, Engineering, Math)</option>
                                            <option value="SOCIAL_SCIENCES">Social Sciences</option>
                                            <option value="ARTS_SPORTS">Arts and Sports Science</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Scholar Selector (Guardian Role Only) */}
                        {formData.role === 'parent' && (
                            <div className="space-y-4 pb-32"> {/* Added padding for dropdown scrollability */}
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Linked Scholars (Multi-Select)</label>
                                
                                {/* Selected Students Chips */}
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {formData.student_ids.length > 0 ? (
                                        formData.student_ids.map(id => {
                                            const student = students.find(s => s.db_id === id);
                                            return (
                                                <div key={id} className="flex items-center gap-2 px-3 py-2 bg-[#18216D] text-white rounded-xl shadow-md animate-in zoom-in-95 duration-200">
                                                    <div className="w-5 h-5 rounded-md bg-[#FFC425] text-[#18216D] flex items-center justify-center text-[9px] font-black uppercase">
                                                        {student?.name?.charAt(0) || 'S'}
                                                    </div>
                                                    <span className="text-[10px] font-black uppercase tracking-tight truncate max-w-[120px]">
                                                        {student?.name || 'Unknown'}
                                                    </span>
                                                    <button 
                                                        type="button"
                                                        onClick={() => toggleStudent(id)}
                                                        className="hover:text-rose-400 transition-colors"
                                                    >
                                                        <i className="fas fa-times-circle"></i>
                                                    </button>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="w-full p-4 bg-slate-50 border border-dashed border-slate-200 rounded-2xl text-center">
                                            <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">No Scholars Linked Yet</p>
                                        </div>
                                    )}
                                </div>

                                <div className="relative" ref={dropdownRef}>
                                    <div className="relative group">
                                        <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 text-xs"></i>
                                        <input
                                            type="text"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            onFocus={() => setShowDropdown(true)}
                                            placeholder="Search and link students..."
                                            className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 shadow-sm rounded-2xl focus:ring-2 focus:ring-[#18216D]/10 font-bold text-sm transition-all"
                                        />
                                        {showDropdown && (
                                            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-[2rem] shadow-2xl border border-slate-100 z-50 overflow-hidden max-h-[320px] flex flex-col animate-in slide-in-from-top-2 duration-300">
                                                <div className="p-4 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
                                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Select Scholars</span>
                                                    <button 
                                                        type="button" 
                                                        onClick={() => setShowDropdown(false)}
                                                        className="text-[9px] font-black text-[#18216D] uppercase hover:underline"
                                                    >
                                                        Done
                                                    </button>
                                                </div>
                                                <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                                                    {fetchingStudents ? (
                                                        <div className="text-center py-10 text-[9px] font-black text-slate-400 uppercase animate-pulse">Querying Database...</div>
                                                    ) : filteredStudents.length === 0 ? (
                                                        <div className="text-center py-10 text-[9px] font-black text-slate-300">No Scholars Found</div>
                                                    ) : (
                                                        filteredStudents.map(student => {
                                                            const isSelected = formData.student_ids.includes(student.db_id);
                                                            return (
                                                                <button
                                                                    key={student.id}
                                                                    type="button"
                                                                    onClick={() => toggleStudent(student.db_id)}
                                                                    className={`w-full flex items-center gap-4 p-3 rounded-xl transition-all ${
                                                                        isSelected ? 'bg-indigo-50/50' : 'hover:bg-slate-50'
                                                                    }`}
                                                                >
                                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-[10px] ${
                                                                        isSelected ? 'bg-[#18216D] text-white shadow-lg' : 'bg-slate-100 text-slate-400'
                                                                    }`}>
                                                                        {student.name.charAt(0)}
                                                                    </div>
                                                                    <div className="text-left flex-1 min-w-0">
                                                                        <p className={`text-[11px] font-black truncate leading-tight ${
                                                                            isSelected ? 'text-[#18216D]' : 'text-slate-600'
                                                                        }`}>{student.name}</p>
                                                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">{student.system_id}</p>
                                                                    </div>
                                                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                                                        isSelected ? 'bg-[#FFC425] border-[#FFC425]' : 'border-slate-200'
                                                                    }`}>
                                                                        {isSelected && <i className="fas fa-check text-[8px] text-[#18216D]"></i>}
                                                                    </div>
                                                                </button>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </form>

                {/* Footer Actions */}
                <div className="p-8 bg-slate-50/50 flex-shrink-0 flex items-center justify-end gap-6 border-t border-slate-50">
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-8 py-4 text-slate-400 font-black text-[10px] uppercase tracking-[0.2em] hover:text-slate-600 transition-all active:scale-95"
                        >
                            Dismiss
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="px-10 py-4 bg-[#18216D] text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transform hover:scale-[1.02] active:scale-95 transition-all shadow-xl shadow-indigo-900/10 disabled:opacity-50 flex items-center gap-3"
                        >
                            {submitting ? (
                                <>
                                    <i className="fas fa-circle-notch animate-spin"></i>
                                    Processing
                                </>
                            ) : isEdit ? 'Update Profile' : 'Authorize Access'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserRegistrationModal;
